import type { FinishedUnaryCall, UnaryCall } from '@protobuf-ts/runtime-rpc'
import { K2ResponseType } from 'shared/utils/errors'
import { processK2Response } from '../client-utils'
import {
  ServerActionResponse,
  ServerActionUseCase,
  ServerActionUseCaseOptions,
} from 'shared/services/server-action'
import { K2ServerActionUseCaseResponse } from './k2-use-case.schema'

type RequestOptions = {
  accessToken?: string
}

type K2RpcCall<ResponseType extends K2ResponseType<object>> =
  | UnaryCall<object, ResponseType>
  | FinishedUnaryCall<object, ResponseType>

type NewInnerCallback<
  RequestInput extends object,
  ResponseType extends K2ResponseType<object>,
> = (
  request: RequestInput,
  options?: RequestOptions,
) => K2RpcCall<ResponseType> | PromiseLike<K2RpcCall<ResponseType>>

type NewOuterCallback<
  RequestInput extends object,
  ResponseType extends K2ResponseType<object>,
> = (
  request: RequestInput,
  options?: RequestOptions,
) => Promise<ServerActionResponse<K2ServerActionUseCaseResponse<ResponseType>>>

/**
 * This method wraps a ServerActionUseCase to return a standardized K2-grpc response
 * - Responses are processed to be used as GuaranteedResponses
 */
export function K2ServerActionUseCase<
  RequestInput extends object,
  ResponseType extends K2ResponseType<object>,
>(
  callback: NewInnerCallback<RequestInput, ResponseType>,
  options: ServerActionUseCaseOptions<RequestInput>,
): NewOuterCallback<RequestInput, ResponseType> {
  return ServerActionUseCase<
    RequestInput,
    K2ServerActionUseCaseResponse<ResponseType>
  >(
    async (
      request,
      options,
    ): Promise<K2ServerActionUseCaseResponse<ResponseType>> => {
      const response = await callback(request, options)
      return processK2Response(response)
    },
    options,
  )
}
